import { uniqBy } from 'lodash';

import { UrlBuilder } from 'tools';
import { ApiRequest, PagedList, PagedListPayload } from 'framework/api';
import { RulesConfiguration } from 'controls/queryBuilder/ruleDefinition';
import { isConditionComplete } from 'controls/queryBuilder/utils/isConditionComplete';
import { getConditionFields } from 'controls/queryBuilder/utils/getConditionFields';
import { GridDataItem } from 'controls/grid/gridDataItem';
import { DataSource } from 'areas/data-sources/types/dataSource';
import { DataSourceQuery, DataSourceRowsPage } from 'areas/data-sources/types/dataSourceQuery';
import { DataSourceExportFormat, DataSourceExportItem } from 'areas/data-sources/types/dataSourceExport';
import { getDataSourcePayload } from 'controls/designer/features/widgets/datasources/api';
import { MultiSelectEntry } from 'controls/queryBuilder/types';

const endpoint = 'dataSources';
const endpointV2 = `${endpoint}/v2`;

// the server returns at most its own row limit anyway; this only asks for everything in one page
const ALL_ROWS = 9999;

export type GetDataSourcesArgs = {
	accountId?: string;
	payload?: PagedListPayload;
	includeSubaccounts?: boolean;
};

export function getDataSources(args?: GetDataSourcesArgs) {
	return new ApiRequest<PagedList<GridDataItem<DataSource>>>({
		url: endpoint + '/summaryList',
		accountBased: true,
		accountId: args?.accountId,
		subaccountsFilter: true,
		includeSubaccounts: args.includeSubaccounts,
		method: 'POST',
		payload: args?.payload,
	});
}

export function getDataSourcesList(accountId: string) {
	return new ApiRequest<DataSource[]>({
		url: endpoint,
		accountBased: true,
		accountId,
		subaccountsFilter: true,
	});
}

// the datasources other accounts share with this one, without its own
export function getSharedDataSourcesList(accountId: string) {
	const urlBuilder = new UrlBuilder(endpoint);
	urlBuilder.add('shared', true);
	return new ApiRequest<DataSource[]>({
		url: urlBuilder.build(),
		accountBased: true,
		accountId,
	});
}

export type GetDataSourceArgs = {
	id: string;
	accountId?: string;
};

export function getDataSource(args: GetDataSourceArgs) {
	return new ApiRequest<DataSource>({
		url: `${endpoint}/${args.id}`,
		accountBased: true,
		accountId: args?.accountId,
	});
}

export function getDataSourceFiltersConfiguration() {
	const urlBuilder = new UrlBuilder(`${endpoint}/summaryList/ruleConfiguration`);
	return new ApiRequest<RulesConfiguration>({
		url: urlBuilder.build(),
		accountBased: true,
		method: 'GET',
	});
}

export type GetDataSourceFiltersConfigurationArgs = {
	accountId: string;
	dataSourceId: string;
	requestId?: string;
	// not pivoted
	categoryColumns?: string[];
	dataColumns?: string[];
	dataRows?: string[];
	// pivoted
	pivotRows?: string[];
	pivotColumns?: string[];
	pivotValues?: string[];
	pivotData?: boolean;
	pivotMatrix?: boolean;
	splitPivotValues?: boolean;
};

export function getDataSourceDataFiltersConfiguration(args: GetDataSourceFiltersConfigurationArgs) {
	const urlBuilder = new UrlBuilder(`dataSources/${args.dataSourceId}/ruleConfiguration`);
	return new ApiRequest<RulesConfiguration>({
		url: urlBuilder.build(),
		accountId: args.accountId,
		accountBased: true,
		method: 'POST',
		payload: getDataSourcePayload(args),
	});
}

export type GetDataSourceDataArgs = GetDataSourceFiltersConfigurationArgs;

export type GridItemType = { [id: string]: string | number };

export function getDataSourceData(args: GetDataSourceDataArgs) {
	return new ApiRequest<PagedList<GridDataItem<GridItemType>>>({
		url: `dataSources/${args.dataSourceId}/data`,
		accountBased: true,
		accountId: args.accountId,
		method: 'POST',
		payload: getDataSourcePayload(args),
	});
}

export function getDataSourceColumns(query: DataSourceQuery, requestId: string, pivoted = false) {
	const { accountId, dataSourceId } = query;
	return new ApiRequest<RulesConfiguration>({
		url: `${endpointV2}/${dataSourceId}/ruleConfiguration`,
		accountBased: true,
		accountId,
		method: 'POST',
		payload: {
			requestId,
			...getScriptPayload(query),
			...getDrilldownPayload(query),
			...(pivoted ? { ...getColumnsPayload(query), ...getTimePayload(query) } : {}),
		},
	});
}

export function getDataSourceColumnValues(
	query: DataSourceQuery,
	requestId: string,
	pivoted: boolean,
	filterColumn: string,
) {
	const { accountId, dataSourceId } = query;
	return new ApiRequest<PagedList<MultiSelectEntry>>({
		url: `${endpointV2}/${dataSourceId}/filterValues`,
		accountBased: true,
		accountId,
		method: 'POST',
		payload: {
			requestId,
			...getScriptPayload(query),
			...getDrilldownPayload(query),
			...(pivoted ? getColumnsPayload(query) : {}),
			...getTimePayload(query),
			filterColumn,
		},
	});
}

export function getDataSourceRows(query: DataSourceQuery, requestId: string) {
	const { accountId, dataSourceId } = query;
	return new ApiRequest<DataSourceRowsPage>({
		url: `${endpointV2}/${dataSourceId}/data`,
		accountBased: true,
		accountId,
		method: 'POST',
		payload: { requestId, ...getRowsPayload(query) },
		convertResultData: fromDataMatrix,
	});
}

export function getDataSourceExportFormats() {
	return new ApiRequest<DataSourceExportFormat[]>({
		url: `${endpointV2}/exportFormats`,
		accountBased: true,
	});
}

// one file with the rows of each under its name: sheets of a workbook or files of an archive
export function exportDataSources(items: DataSourceExportItem[], format: string) {
	return new ApiRequest<string, Record<string, unknown>[]>({
		url: `${endpointV2}/export${format}`,
		accountBased: true,
		method: 'POST',
		payload: items.map(({ query, requestId, name }) => ({
			requestId,
			accountId: query.accountId,
			dataSourceId: query.dataSourceId,
			exportFilename: name,
			...getRowsPayload(query),
		})),
	});
}

function getRowsPayload(query: DataSourceQuery) {
	const { filter, sort, limit } = query;
	return {
		...getScriptPayload(query),
		...getDrilldownPayload(query),
		...getColumnsPayload(query),
		...getTimePayload(query),
		filter: isConditionComplete(filter) ? filter : {},
		sort: sort.column ? [{ field: sort.column, dir: sort.descending ? 'desc' : 'asc' }] : [],
		skip: 0,
		take: limit ?? ALL_ROWS,
	};
}

type DataSourceMatrixPage = PagedList<GridDataItem<(string | number)[]>> & { columns: string[] };

// a function of its own rather than one made with each request: the requests are compared structurally
function fromDataMatrix(data: unknown): DataSourceRowsPage {
	const { columns, items, ...page } = data as DataSourceMatrixPage;
	return {
		...page,
		items: items.map((item) => ({
			...item,
			data: Object.fromEntries(columns.map((column, index) => [column, item.data[index]])),
		})),
	};
}

// the columns come from a run of the script as well as the rows; a line with no key yet is still being typed in,
// and of those with the same key the first counts
function getScriptPayload({ scriptParameters }: DataSourceQuery) {
	const parameters = uniqBy(
		(scriptParameters ?? []).filter((parameter) => parameter.key.trim() != ''),
		(parameter) => parameter.key.trim(),
	);
	return parameters.length > 0 ? { scriptParameters: parameters } : {};
}

// the rows are kept by their time before they are pivoted, so the columns of a pivot and the values found in a column
// depend on it too, while the columns of the rows themselves don't
function getTimePayload({ period }: DataSourceQuery) {
	return period ? { timePeriodColumn: period.column, timePeriod: period.period } : {};
}

function getDrilldownPayload({ drilldown }: DataSourceQuery) {
	if (drilldown == null) {
		return {};
	}

	const { row, field, settings } = drilldown;
	return {
		drillDownData: row,
		drillDownField: field,
		drillDownSettings: settings,
		// a script is told the clicked chart was inverted by this one, not by the settings; the rows behind a click
		// are not inverted by it
		pivotData: settings.pivotData,
	};
}

function getColumnsPayload({ xColumn, columns, filter, sort, pivot, invert, drilldown }: DataSourceQuery) {
	// a script makes the rows behind a click with columns of its own
	if (drilldown != null) {
		return {};
	}

	if (pivot.enabled) {
		return {
			pivotMatrix: true,
			pivotRows: pivot.rows,
			pivotColumns: pivot.columns,
			pivotValues: columns,
			splitPivotValues: pivot.split,
			pivotData: invert,
		};
	}

	if (invert) {
		return { pivotData: true, dataColumns: columns.length > 0 ? columns : undefined };
	}

	const looked = isConditionComplete(filter) ? getConditionFields(filter) : [];
	return {
		dataColumns:
			columns.length > 0
				? [...new Set([xColumn, ...columns, ...looked, sort.column].filter(Boolean))]
				: undefined,
	};
}

export function releaseDataSourceRequest(accountId: string, requestId: string) {
	return new ApiRequest<unknown>({
		url: `${endpointV2}/release/${requestId}`,
		accountBased: true,
		accountId,
		method: 'GET',
	});
}
